import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency, CDPercentage } from '../primitives/CDCurrency';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface LoanCalculationsData {
  totalOfPayments: number;
  financeCharge: number;
  amountFinanced: number;
  annualPercentageRate: number;
  totalInterestPercentage: number;
}

export interface CDLoanCalculationsProps {
  /** Loan calculations data */
  data: Partial<LoanCalculationsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<LoanCalculationsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDLoanCalculations - Loan Calculations section on Page 5
 *
 * Shows key loan metrics: Total of Payments, Finance Charge, Amount Financed, APR, TIP
 */
export const CDLoanCalculations = React.forwardRef<HTMLDivElement, CDLoanCalculationsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update a field
    const updateField = (field: keyof LoanCalculationsData, value: number) => {
      onChange?.({ ...data, [field]: value });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Loan Calculations" />

        <div className="border border-t-0 border-cd-black">
          <div className="grid grid-cols-5 gap-0">
            {/* Total of Payments */}
            <div className="border-r border-cd-black p-1.5 text-center">
              <div className="cd-text-small font-bold mb-0.5">Total of Payments</div>
              <div className="text-[11pt] font-bold">
                <CDCurrency
                  value={data.totalOfPayments}
                  editable={editable}
                  onChange={(v) => updateField('totalOfPayments', v)}
                />
              </div>
              <div className="cd-text-tiny text-cd-gray-text mt-0.5">
                Total you will have paid after you make all payments of principal, interest,
                mortgage insurance, and loan costs, as scheduled.
              </div>
            </div>

            {/* Finance Charge */}
            <div className="border-r border-cd-black p-1.5 text-center">
              <div className="cd-text-small font-bold mb-0.5">Finance Charge</div>
              <div className="text-[11pt] font-bold">
                <CDCurrency
                  value={data.financeCharge}
                  editable={editable}
                  onChange={(v) => updateField('financeCharge', v)}
                />
              </div>
              <div className="cd-text-tiny text-cd-gray-text mt-0.5">
                The dollar amount the loan will cost you.
              </div>
            </div>

            {/* Amount Financed */}
            <div className="border-r border-cd-black p-1.5 text-center">
              <div className="cd-text-small font-bold mb-0.5">Amount Financed</div>
              <div className="text-[11pt] font-bold">
                <CDCurrency
                  value={data.amountFinanced}
                  editable={editable}
                  onChange={(v) => updateField('amountFinanced', v)}
                />
              </div>
              <div className="cd-text-tiny text-cd-gray-text mt-0.5">
                The loan amount available after paying your upfront finance charge.
              </div>
            </div>

            {/* Annual Percentage Rate (APR) */}
            <div className="border-r border-cd-black p-1.5 text-center">
              <div className="cd-text-small font-bold mb-0.5">Annual Percentage Rate (APR)</div>
              <div className="text-[11pt] font-bold">
                <CDPercentage
                  value={data.annualPercentageRate}
                  editable={editable}
                  onChange={(v) => updateField('annualPercentageRate', v)}
                />
              </div>
              <div className="cd-text-tiny text-cd-gray-text mt-0.5">
                Your costs over the loan term expressed as a rate. This is not your interest rate.
              </div>
            </div>

            {/* Total Interest Percentage (TIP) */}
            <div className="p-1.5 text-center">
              <div className="cd-text-small font-bold mb-0.5">Total Interest Percentage (TIP)</div>
              <div className="text-[11pt] font-bold">
                <CDPercentage
                  value={data.totalInterestPercentage}
                  editable={editable}
                  onChange={(v) => updateField('totalInterestPercentage', v)}
                />
              </div>
              <div className="cd-text-tiny text-cd-gray-text mt-0.5">
                The total amount of interest that you will pay over the loan term as a percentage of
                your loan amount.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDLoanCalculations.displayName = 'CDLoanCalculations';
